import { useState, useContext, useRef, useEffect } from 'react'
import { useStaticQuery, graphql, navigate } from 'gatsby'
import pluralize from 'pluralize'
import format from 'date-fns/format'
import ModalDialog from 'components/common/modalDialog'
import { StyledButton } from 'components/common/typography'
import { AvailabilityContext } from 'components/booking/context/availabilityContext'
import { CartContext } from 'components/booking/context/cartContext'
import BookingWidgetDateDropdown from './bookingWidgetDateDropdown'
import BookingWidgetGuestDropdown from './bookingWidgetGuestDropdown'
import { folioHasCampsites, getAvailableUnits } from 'components/booking/context/bookingUtils'
import { MainContainer, GuestText, DateErrorText, DropdownButton, DialogBody } from './bookingWidget_styles'

const BookingWidget = ({ home = false }) => {
  const { cartFolio } = useContext(CartContext)
  const { prismicRentals: { data } } = useStaticQuery(query)
  const { startDate, setStartDate, endDate, setEndDate, adultCount, setAdultCount, childCount, setChildCount, bringingDogs, setBringingDogs, adaAccessible, setAdaAccessible, searchAvailability } = useContext(AvailabilityContext)
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false)
  const [guestDropdownOpen, setGuestDropdownOpen] = useState(false)
  const [dateDropdownOffset, setDateDropdownOffset] = useState(0)
  const [guestDropdownOffset, setGuestDropdownOffset] = useState(0)
  const [showDateError, setShowDateError] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const guestDropdownButtonRef = useRef()
  const dateDropdownButtonRef = useRef()

  useEffect(() => {
    setDateDropdownOffset(dateDropdownButtonRef.current?.offsetLeft - 10)
    setGuestDropdownOffset(guestDropdownButtonRef.current?.offsetLeft - 10)
  }, [])

  const formattedGuestCount = () => {
    const adultText = pluralize('adult', adultCount, true)
    const childText = pluralize('child', childCount, true)
    return `${adultText}, ${childText}`
  }

  const submit = async(event) => {
    event.preventDefault()
    if (!startDate || !endDate) {
      setShowDateError(true)
      return
    }
    setShowDateError(false)
    const availability = await searchAvailability(cartFolio)
    const availableUnits = getAvailableUnits(availability.filter(group => group.type === 'site'))
    if (availableUnits.length === 0) {
      setDialogOpen(true)
    }
    else {
      navigate('/booking')
    }
  }

  const dateText = () => {
    if (!startDate || !endDate) {
      return 'Select dates'
    }
    if (format(startDate, 'MMM') !== format(endDate, 'MMM')) {
      return `${format(startDate, 'MMM dd')} - ${format(endDate, 'MMM dd')}`
    }
    return `${format(startDate, 'MMM dd')} - ${format(endDate, 'dd')}`
  }

  const onSetStartDate = newStartDate => {
    setStartDate(newStartDate)
    setShowDateError(false)
  }

  const onSetEndDate = newEndDate => {
    setEndDate(newEndDate)
    setShowDateError(false)
  }

  const onDateDropdownClose = () => {
    setDateDropdownOpen(false)
    dateDropdownButtonRef.current?.focus()
  }

  const onGuestDropdownClose = () => {
    setGuestDropdownOpen(false)
    guestDropdownButtonRef.current?.focus()
  }

  const onDialogChangeDates = () => {
    setDialogOpen(false)
    setDateDropdownOpen(true)
  }

  return (
    <MainContainer onSubmit={submit}>
      <DropdownButton
        onClick={() => setDateDropdownOpen(oldVal => !oldVal)}
        aria-label='date dropdown'
        ref={dateDropdownButtonRef}
      >
        <p>Dates</p>
        <GuestText disabled={!startDate || !endDate || folioHasCampsites(cartFolio)} error={showDateError}>
          {dateText()}
          {showDateError && <DateErrorText>This field is required</DateErrorText>}
        </GuestText>
      </DropdownButton>
      {dateDropdownOpen &&
        <BookingWidgetDateDropdown
          startDate={startDate}
          setStartDate={onSetStartDate}
          endDate={endDate}
          setEndDate={onSetEndDate}
          close={onDateDropdownClose}
          $offset={dateDropdownOffset}
        />
      }
      <DropdownButton
        onClick={() => setGuestDropdownOpen(oldVal => !oldVal)}
        aria-label='guest dropdown'
        ref={guestDropdownButtonRef}
      >
        <p>Guests</p>
        <GuestText>
          {formattedGuestCount()}
        </GuestText>
      </DropdownButton>
      {guestDropdownOpen &&
        <BookingWidgetGuestDropdown
          adultCount={adultCount}
          setAdultCount={setAdultCount}
          childCount={childCount}
          setChildCount={setChildCount}
          bringingDogs={bringingDogs}
          setBringingDogs={setBringingDogs}
          adaAccessible={adaAccessible}
          setAdaAccessible={setAdaAccessible}
          close={onGuestDropdownClose}
          $offset={guestDropdownOffset}
        />
      }
      <StyledButton $black>{home ? 'Book now' : 'Search'}</StyledButton>
      <ModalDialog title={data.no_availability_popup_title} isOpen={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogBody>{data.no_availability_popup_body}</DialogBody>
        <StyledButton $black type='button' onClick={onDialogChangeDates}>{data.no_availability_popup_button_title}</StyledButton>
      </ModalDialog>
    </MainContainer>
  )
}

export default BookingWidget

export const query = graphql`
  query {
    prismicRentals {
      data {
        no_availability_popup_title
        no_availability_popup_body
        no_availability_popup_button_title
      }
    }
  }
`